import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ScreenProps } from '../../app/routes';
import { useRouter } from '../../app/router';
import { useAuth } from '../../auth/AuthProvider';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import { ApiError } from '../documents/api';
import { loadPeriods } from '../documents/api';
import type { DocumentPeriod } from '../documents/filters';
import {
  approveDocument,
  cancelDocument,
  createDocument,
  deleteAttachment,
  emptyFormState,
  linesToInput,
  loadAccounts,
  loadAttachments,
  loadChain,
  loadDocument,
  loadDocumentTypes,
  patchDocument,
  postDocument,
  toAccountOptions,
  toAttachmentViews,
  toFormState,
  toTypeView,
  uploadAttachment,
} from './api';
import type { AccountDto, AttachmentDto, DocumentAction, DocumentDto, DocumentTypeDto } from './api';
import { DocumentFormView } from './DocumentFormView';
import type { ChainGraphView } from './DocumentFormView';
import type { DocumentFormState, FormLine } from './form';

const EMPTY_CHAIN: ChainGraphView = { up: [], down: [] };

function todayIso(): string {
  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function periodOf(periods: readonly DocumentPeriod[], date: string): DocumentPeriod | undefined {
  const key = date.slice(0, 7);
  return (
    periods.find((period) => `${period.year}-${period.month.toString().padStart(2, '0')}` === key) ?? periods[0]
  );
}

function problemOf(error: unknown): Problem {
  if (error instanceof ApiError) {
    return error.problem;
  }
  return { status: 0, code: 'network', detail: error instanceof Error ? error.message : String(error) };
}

function newLineKey(): string {
  const random = globalThis.crypto?.randomUUID?.();
  return random ?? `line-${Date.now()}-${Math.round(Math.random() * 100000)}`;
}

export default function DocumentFormScreen({ params }: ScreenProps): JSX.Element {
  const { authorizedFetch, session } = useAuth();
  const { language, t } = useI18n();
  const { navigate } = useRouter();
  const documentId = params.id;

  const [types, setTypes] = useState<DocumentTypeDto[]>([]);
  const [accounts, setAccounts] = useState<AccountDto[]>([]);
  const [periods, setPeriods] = useState<DocumentPeriod[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [form, setForm] = useState<DocumentFormState | null>(null);
  const [chain, setChain] = useState<ChainGraphView>(EMPTY_CHAIN);
  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const actor = session?.user.id ?? '';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [loadedTypes, loadedAccounts, loadedPeriods] = await Promise.all([
          loadDocumentTypes(authorizedFetch),
          loadAccounts(authorizedFetch),
          loadPeriods(authorizedFetch),
        ]);
        if (!cancelled) {
          setTypes(loadedTypes);
          setAccounts(loadedAccounts);
          setPeriods(loadedPeriods);
        }
      } catch (error) {
        if (!cancelled) {
          setProblem(problemOf(error));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const reloadDocument = useCallback(
    async (id: string) => {
      const [document, graph, files] = await Promise.all([
        loadDocument(authorizedFetch, id),
        loadChain(authorizedFetch, id),
        loadAttachments(authorizedFetch, id),
      ]);
      setForm(toFormState(document));
      setChain(graph);
      setAttachments(files);
    },
    [authorizedFetch],
  );

  useEffect(() => {
    if (documentId === undefined) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const document: DocumentDto = await loadDocument(authorizedFetch, documentId);
        const [graph, files] = await Promise.all([
          loadChain(authorizedFetch, documentId),
          loadAttachments(authorizedFetch, documentId),
        ]);
        if (!cancelled) {
          setForm(toFormState(document));
          setChain(graph);
          setAttachments(files);
          setProblem(null);
        }
      } catch (error) {
        if (!cancelled) {
          setProblem(problemOf(error));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, documentId]);

  useEffect(() => {
    if (documentId !== undefined || types.length === 0) {
      return;
    }
    const type = types.find((candidate) => candidate.code === selectedType) ?? types[0];
    if (type === undefined) {
      return;
    }
    setForm((current) =>
      current !== null && current.type === type.code ? current : emptyFormState(type, todayIso()),
    );
  }, [types, selectedType, documentId]);

  const typeDto = useMemo(
    () => types.find((candidate) => candidate.code === form?.type),
    [types, form?.type],
  );

  const apply = async (action: DocumentAction): Promise<void> => {
    setSaving(true);
    setProblem(null);
    try {
      const saved = await action();
      if (documentId === undefined) {
        navigate(`/documents/${saved.id}`);
      } else {
        await reloadDocument(documentId);
      }
    } catch (error) {
      setProblem(problemOf(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading || form === null || typeDto === undefined) {
    return (
      <section>
        <p className="muted" data-testid="document-form-loading">
          {t('documentFormLoading')}
        </p>
        {problem !== null ? (
          <div className="alert alert--danger" role="alert">
            <code>{problem.code}</code> — {problem.detail}
          </div>
        ) : null}
      </section>
    );
  }

  const typeView = toTypeView(typeDto, language);
  const accountOptions = toAccountOptions(accounts, language);
  const period = periodOf(periods, form.date);
  const orgId = period?.orgId ?? '';

  const setHeader = (name: string, value: string): void => {
    setForm((current) => (current === null ? current : { ...current, data: { ...current.data, [name]: value } }));
  };

  const setLine = (lineKey: string, field: string, value: string): void => {
    setForm((current) => {
      if (current === null) {
        return current;
      }
      return {
        ...current,
        lines: current.lines.map((line: FormLine) =>
          line.key !== lineKey
            ? line
            : field === 'amount'
              ? { ...line, amount: value }
              : { ...line, data: { ...line.data, [field]: value } },
        ),
      };
    });
  };

  const setDimension = (lineKey: string, slot: number, value: string): void => {
    setForm((current) => {
      if (current === null) {
        return current;
      }
      return {
        ...current,
        lines: current.lines.map((line: FormLine) =>
          line.key !== lineKey
            ? line
            : { ...line, dimensions: { ...line.dimensions, [`dim${slot}`]: value } },
        ),
      };
    });
  };

  const addLine = (): void => {
    setForm((current) => {
      if (current === null) {
        return current;
      }
      const maxLineNo = current.lines.reduce((max, line) => Math.max(max, line.lineNo), 0);
      return {
        ...current,
        lines: [
          ...current.lines,
          { key: newLineKey(), lineNo: maxLineNo + 1, data: {}, amount: '0.00', dimensions: {} },
        ],
      };
    });
  };

  const removeLine = (lineKey: string): void => {
    setForm((current) =>
      current === null ? current : { ...current, lines: current.lines.filter((line) => line.key !== lineKey) },
    );
  };

  return (
    <>
      {documentId === undefined ? (
        <label className="field" style={{ maxWidth: 320, marginBottom: 12 }}>
          <span>{t('documentFormTypeLabel')}</span>
          <select
            className="input"
            value={form.type}
            onChange={(event) => setSelectedType(event.target.value)}
          >
            {types.map((type) => (
              <option key={type.code} value={type.code}>
                {type.nameI18n[language]}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <DocumentFormView
        type={typeView}
        document={form}
        accounts={accountOptions}
        permissions={session?.user.permissions ?? []}
        chain={chain}
        attachments={toAttachmentViews(attachments)}
        problem={problem}
        saving={saving}
        onHeaderChange={setHeader}
        onLineChange={setLine}
        onDimensionChange={setDimension}
        onAddLine={addLine}
        onRemoveLine={removeLine}
        onSave={() => {
          void apply(() =>
            documentId === undefined
              ? createDocument(authorizedFetch, {
                  actor,
                  orgId,
                  type: form.type,
                  operationKind: form.operationKind,
                  date: form.date,
                  data: form.data,
                  lines: linesToInput(form.lines),
                })
              : patchDocument(authorizedFetch, documentId, {
                  actor,
                  operationKind: form.operationKind,
                  data: form.data,
                  lines: linesToInput(form.lines),
                }),
          );
        }}
        onApprove={() => {
          if (documentId !== undefined) {
            void apply(() => approveDocument(authorizedFetch, documentId, actor));
          }
        }}
        onPost={() => {
          if (documentId !== undefined) {
            void apply(() => postDocument(authorizedFetch, documentId, actor));
          }
        }}
        onCancel={(reason) => {
          if (documentId !== undefined) {
            void apply(() => cancelDocument(authorizedFetch, documentId, actor, reason));
          }
        }}
        onOpenDocument={(id) => navigate(`/documents/${id}`)}
        onUpload={(file) => {
          void (async () => {
            setSaving(true);
            setProblem(null);
            try {
              await uploadAttachment(authorizedFetch, documentId ?? '', file);
              if (documentId !== undefined) {
                setAttachments(await loadAttachments(authorizedFetch, documentId));
              }
            } catch (error) {
              setProblem(problemOf(error));
            } finally {
              setSaving(false);
            }
          })();
        }}
        onDeleteAttachment={(attachmentId) => {
          void (async () => {
            setSaving(true);
            setProblem(null);
            try {
              await deleteAttachment(authorizedFetch, attachmentId);
              if (documentId !== undefined) {
                setAttachments(await loadAttachments(authorizedFetch, documentId));
              }
            } catch (error) {
              setProblem(problemOf(error));
            } finally {
              setSaving(false);
            }
          })();
        }}
      />
    </>
  );
}
